import { Component, computed, effect, ViewChild, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { form, FormField, min, validate } from '@angular/forms/signals';
import { ServicesSettingsGeneralInfoPageStore } from './services-settings-general-info-page.store';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import {
    activeColumn,
    colorColumn,
    dateFromColumn,
    dateToColumn,
    descriptionColumn,
    nameColumn,
} from '../../../../shared/components/kit/table/table.fields';
import {
    ServiceWorkgroupModalComponent,
} from '../../../components/services/settings/service-workgroup-modal/service-workgroup-modal.component';
import {
    ServiceOperationalCapacityModalComponent,
} from '../../../components/services/settings/service-operational-capacity-modal/service-operational-capacity-modal.component';
import { ServiceWorkgroupObject } from '@libs/types/src/services/object-type/service/settings/service-workgroup.object';
import {
    ServiceOperationalCapacityObject,
} from '@libs/types/src/services/object-type/service/settings/service-operational-capacity.object';
import { dateify } from '../../../../shared/helpers/date.helper';
import { isInvalid } from '../../../../shared/helpers/validate';
import { FieldErrorFactory } from '../../../../shared/service/field-error.service';
import { TableColumnType } from '../../../../shared/components/kit/table/table.types';
import {
    serviceOperationalCapacityUnitEnumOptions,
} from '@libs/types/src/services/enum/service-operational-capacity-unit.enum';
import {
    ServiceWidgetsSettingsComponent,
} from '../../../components/services/settings/service-widgets-settings/service-widgets-settings.component';
import { routeParams } from '../../../../shared/shared.global-service';
import { ServiceTypeEnum } from '../../../../../../../../types/src/services/enum/service-type.enum';
import { CardComponent } from '../../../../../components/kit/card/card.component';
import { ColorInputComponent } from '../../../../../components/kit/input/color-input/color-input.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { CardTableComponent } from '../../../../shared/components/kit/table/card-table.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { NumberInputComponent } from '../../../../../components/kit/input/text-input-v2/number-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { featureOn } from '../../../../../feature-flag';
import {
    ServiceWaitingListObject,
} from '@libs/types/src/services/object-type/service/settings/service-waiting-list.object';
import {
    ServiceWaitingListModalComponent,
} from '../../../components/services/settings/service-waiting-list-modal/service-waiting-list-modal.component';
import { serviceWaitingListUnitEnumOptions } from '@libs/types/src/services/enum/service-waiting-list-unit.enum';
import { ServiceArchiveSettingsForm } from './services-settings-general-info-page.types';
import { BackgroundTaskLaunchService } from '../../../../layout/store/background-tasks/background-task-launch.service';
import { BackgroundActionType } from '@libs/types/src/notifications/types/background-action.types';

@Component({
    selector: 'app-services-settings-general-info-page',
    templateUrl: 'services-settings-general-info-page.component.html',
    styleUrl: 'services-settings-general-info-page.component.scss',
    providers: [ServicesSettingsGeneralInfoPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardComponent,
        ServiceWidgetsSettingsComponent,
        ColorInputComponent,
        TextInputComponent,
        CardTableComponent,
        AsyncPipe,
        NumberInputComponent,
        TranslatePipe,
        CheckInputComponent,
        DateInputComponent,
        NgTemplateOutlet,
        ActionButtonsComponent,
        PrimeTemplate,
        ServiceWorkgroupModalComponent,
        ServiceOperationalCapacityModalComponent,
        ServiceWaitingListModalComponent,
        FormField,
    ],
})
export class ServicesSettingsGeneralInfoPageComponent extends SubscriptionComponent {

    protected readonly store = inject(ServicesSettingsGeneralInfoPageStore);
    private readonly fb = inject(FormBuilder);
    private readonly confirm = inject(ConfirmBuilder);
    private readonly backgroundTaskLauncher = inject(BackgroundTaskLaunchService);

    @ViewChild(ServiceWorkgroupModalComponent) workgroupModal!: ServiceWorkgroupModalComponent;
    @ViewChild(ServiceOperationalCapacityModalComponent) operationalCapacitiesModal!: ServiceOperationalCapacityModalComponent;
    @ViewChild(ServiceWaitingListModalComponent) waitingListModal!: ServiceWaitingListModalComponent;
    @ViewChild(ServiceWidgetsSettingsComponent) widgetsSettings!: ServiceWidgetsSettingsComponent;

    protected additionalFilter = computed(() =>
        [[{ column: 'serviceId', operator: 'EQ', value: [routeParams.serviceId()] }]],
    );

    protected form = this.fb.group({
        color: this.fb.control<string | null>(null),
        pohoda: this.fb.group({
            name: this.fb.control<string | null>(null),
            pairSymbol: this.fb.control<string | null>(null),
            prepayment: this.fb.control<string | null>(null),
            division: this.fb.control<string | null>(null),
            center: this.fb.control<string | null>(null),
        }),
        timeRanges: this.fb.array([0, 1, 2, 3, 4, 5].map(() => this.fb.group({
            order: this.fb.control<number>(0),
            active: this.fb.control<boolean>(true),
            from: this.fb.control<Date | null>(null),
            to: this.fb.control<Date | null>(null),
        }))),
        social: this.fb.group({
            useWaitingList: this.fb.control<boolean>(false),
        }),
    });

    protected readonly archiveSettingsModel = signal<ServiceArchiveSettingsForm>({
        archiveAfterYears: null,
        archiveAfterDeathYears: null,
    });
    protected readonly archiveSettingsFields = form(this.archiveSettingsModel, schema => {
        min(schema.archiveAfterYears, 0);
        min(schema.archiveAfterDeathYears, 0);
        validate(schema.archiveAfterYears, ({ value }) => value() !== null && !Number.isInteger(value())
            ? { kind: 'integer' }
            : null);
        validate(schema.archiveAfterDeathYears, ({ value }) => value() !== null && !Number.isInteger(value())
            ? { kind: 'integer' }
            : null);
    });

    protected serviceId = 0;

    protected workgroupColumns = [
        activeColumn,
        colorColumn,
        nameColumn,
        descriptionColumn,
    ];

    protected operationalCapacityColumns = [
        dateFromColumn,
        dateToColumn,
        {
            name: 'name',
            filterable: {
                type: 'select',
                selectOptions: () => serviceOperationalCapacityUnitEnumOptions,
            },
            sortable: true,
            type: TableColumnType.String50,
        },
        descriptionColumn,
        {
            name: 'amount',
            filterable: { type: 'number' },
            sortable: true,
        },
    ];

    protected waitingListColumns = [
        dateFromColumn,
        dateToColumn,
        nameColumn,
        {
            name: 'type',
            filterable: {
                type: 'select',
                selectOptions: () => serviceWaitingListUnitEnumOptions,
            },
            sortable: true,
            type: TableColumnType.String50,
        },
        descriptionColumn,
    ];

    protected isHealthCare = computed(() => this.store.service()?.type === ServiceTypeEnum.HEALTH_CARE);
    protected isSocial = computed(() => this.store.service()?.type === ServiceTypeEnum.SOCIAL);
    protected hasArchiveSettings = computed(() => this.isHealthCare() || this.isSocial());
    protected timeRangeLabel = computed(() => {
        if (this.isHealthCare()) {
            return 'service.settings.general.timeRanges.labelHealthCare';
        }
        if (this.isSocial()) {
            return 'service.settings.general.timeRanges.labelSocial';
        }

        return 'service.settings.general.timeRanges.label';
    });

    constructor() {
        super();
        this.store.refreshGrid('getServiceWorkgroups', 'createServiceWorkgroup', 'updateServiceWorkgroup', 'deleteServiceWorkgroup');
        this.store.refreshGrid('getServiceOperationalCapacities', 'createServiceOperationalCapacity', 'updateServiceOperationalCapacity', 'deleteServiceOperationalCapacity');
        this.store.refreshGrid('getServiceWaitingListItems', 'createServiceWaitingListItem', 'updateServiceWaitingListItem', 'deleteServiceWaitingListItem');
        this.store.onRouteChange('getServiceWorkgroups', { serviceId: 'serviceId' }, { asFilter: true });
        this.store.onRouteChange('getServiceOperationalCapacities', { serviceId: 'serviceId' }, { asFilter: true });

        this.store.registerSuccessMessage('patchService', 'common.updated');
        this.store.registerSuccessMessage('deleteServiceWorkgroup', 'common.deleted');
        this.store.registerSuccessMessage('deleteServiceOperationalCapacity', 'common.deleted');

        effect(() => {
            const service = this.store.service();
            if (service) {
                this.form.patchValue({
                    ...service,
                    timeRanges: service.timeRanges.map((it) => ({
                        order: it.order,
                        from: dateify(it.from),
                        to: dateify(it.to),
                        active: it.active,
                    })),
                    social: {
                        useWaitingList: service.social?.useWaitingList ?? false,
                    },
                });
                this.archiveSettingsFields().reset({
                    archiveAfterYears: service.social?.archiveAfterYears ?? null,
                    archiveAfterDeathYears: service.social?.archiveAfterDeathYears ?? null,
                });
                this.serviceId = service.id;
            }
        });
    }

    protected async submit(): Promise<void> {
        (this.form.controls?.timeRanges as any).controls.forEach((it: FormGroup) => {
            const c = it.controls as Record<string, FormControl>;
            c.to.setErrors(null);
            c.from.setErrors(null);
        });
        if (isInvalid(this.form)) return;
        if (this.hasArchiveSettings() && this.archiveSettingsFields().invalid()) {
            this.archiveSettingsFields().markAsTouched();
            return;
        }

        const intervals: any[] = [];
        let err = false;
        (this.form.controls?.timeRanges as any).controls
            .filter((it: FormGroup) => it.value.active)
            .forEach((it: FormGroup, index: number, activeTimeRanges: FormGroup[]) => {
                const c = it.controls as Record<string, FormControl>;
                if (it.value.active) {
                    if (!it.value.from) {
                        c.from.setErrors({ required: true });
                        err = true;
                    }
                    if (!it.value.to) {
                        c.to.setErrors({ required: true });
                        err = true;
                    }
                    if (!err) {
                        const from = `${it.value.from.getHours().toString().padStart(2, '0')}${it.value.from.getMinutes().toString().padStart(2, '0')}`;
                        const to = `${it.value.to.getHours().toString().padStart(2, '0')}${it.value.to.getMinutes().toString().padStart(2, '0')}`;
                        const isLastActive = index === activeTimeRanges.length - 1;

                        if (to <= from && !isLastActive) {
                            c.to.setErrors(FieldErrorFactory.timeOverlapsError());
                            err = true;
                        } else {
                            intervals.push([from, to]);
                        }
                    }
                }
            });
        if (intervals.length > 1) {
            for (let i = 0; i < intervals.length - 1; i++) {
                const a = intervals[i][1];
                const b = intervals[i + 1][0];

                if (a > b) {
                    (this.form.controls?.timeRanges as any).controls[i + 1].controls.from.setErrors(FieldErrorFactory.timeOverlapsError());
                    err = true;
                }
            }
            const lastTo = intervals[intervals.length - 1][1];
            const firstFrom = intervals[0][0];
            const lastFrom = intervals[intervals.length - 1][0];

            if (lastTo > firstFrom && lastTo < lastFrom) {
                (this.form.controls?.timeRanges as any).controls[intervals.length - 1].controls.to.setErrors(FieldErrorFactory.timeOverlapsError());
                err = true;
            }
        }

        if (err) return;

        this.widgetsSettings.saveWidgetSettings();

        if (this.serviceId) {
            const archiveSettings = this.archiveSettingsModel();
            const currentArchiveSettings = this.store.service()?.social;
            const archiveSettingsChanged = (currentArchiveSettings?.archiveAfterYears ?? null) !== archiveSettings.archiveAfterYears
                || (currentArchiveSettings?.archiveAfterDeathYears ?? null) !== archiveSettings.archiveAfterDeathYears;

            await this.store.api.patchService(
                {
                    id: Number(this.serviceId),
                    ...this.form.value,
                    social: {
                        ...this.form.value.social,
                        archiveAfterYears: archiveSettings.archiveAfterYears,
                        archiveAfterDeathYears: archiveSettings.archiveAfterDeathYears,
                    },
                    timeRanges: this.form.value.timeRanges || [],
                } as any,
            );

            if (archiveSettingsChanged) {
                await this.backgroundTaskLauncher.start({
                    type: BackgroundActionType.FOLDER_ARCHIVE_RECALCULATION,
                    payload: { serviceId: Number(this.serviceId) },
                    alreadyRunningMessage: 'service.settings.general.archive.backgroundTask.alreadyRunning',
                    failedMessage: 'service.settings.general.archive.backgroundTask.startFailed',
                });
            }
        }
    }

    protected createWorkgroup(): void {
        this.workgroupModal?.show();
    }

    protected updateWorkgroup(item: ServiceWorkgroupObject): void {
        this.workgroupModal?.show(true, item);
    }

    protected deleteWorkgroup(item: any): void {
        this.confirm.confirm(
            'common.delete',
            item.item.name,
            () => this.store.api.deleteServiceWorkgroup({ id: item.item.id }),
            null,
            'pi-trash',
            item.event.target,
        );

    }

    protected createOperationalCapacity(): void {
        this.operationalCapacitiesModal?.show();
    }

    protected updateOperationalCapacity(item: ServiceOperationalCapacityObject): void {
        this.operationalCapacitiesModal?.show(true, item);
    }

    protected deleteOperationalCapacity(item: any): void {
        this.confirm.confirm(
            'common.delete',
            item.item.name,
            () => this.store.api.deleteServiceOperationalCapacity({ id: item.item.id }),
            null,
            'pi-trash',
            item.event.target,
        );
    }

    protected createWaitingList(): void {
        this.waitingListModal?.show();
    }

    protected updateWaitingList(item: ServiceWaitingListObject): void {
        this.waitingListModal?.show(true, item);
    }

    protected deleteWaitingList(item: any): void {
        this.confirm.confirm(
            'common.delete',
            item.item.name,
            () => this.store.api.deleteServiceWaitingListItem({ id: item.item.id }),
            null,
            'pi-trash',
            item.event.target,
        );
    }

    protected readonly ServiceTypeEnum = ServiceTypeEnum;
    protected readonly featureOn = featureOn;
}
